import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { bienBanRepository, type LocPhien } from '../../../lib/repositories/bien-ban.repository';
import { thuLai as nenThuLai } from '../../vai-tro/hooks/useVaiTro';
import { canTuLamMoi, coBanChepLoi } from '../nghiep-vu/bien-ban';
import type { AmThanhKy, DinhDangXuat, Doan, DoanSua, Phien, PhienSua, PhienTao, TomTatGhi, TrangPhien, TrangThaiPhien } from '../types';

export const KHOA_BIEN_BAN = {
  goc: ['bien-ban'] as const,
  danhSach: (loc: Omit<LocPhien, 'limit' | 'cursor'>) => ['bien-ban', 'danh-sach', loc] as const,
  dsGoc: ['bien-ban', 'danh-sach'] as const,
  chiTiet: (id: string) => ['bien-ban', 'chi-tiet', id] as const,
  doan: (id: string) => ['bien-ban', 'doan', id] as const,
  amThanh: (id: string) => ['bien-ban', 'am-thanh', id] as const,
};

const MOT_TRANG = 20;
const NHIP_XU_LY = 3_000;

/** Danh sách lọc ở server, phân trang con trỏ; còn phiên đang chạy thì tự hỏi lại, xong hết thì dừng. */
export function useDanhSachPhien(loc: Omit<LocPhien, 'limit' | 'cursor'>) {
  return useInfiniteQuery<TrangPhien, ApiError>({
    queryKey: KHOA_BIEN_BAN.danhSach(loc),
    queryFn: ({ pageParam }) => bienBanRepository.danhSach({ ...loc, limit: MOT_TRANG, cursor: pageParam as string | undefined }),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.next_cursor ?? undefined,
    staleTime: 10_000,
    retry: nenThuLai,
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.items.some(canTuLamMoi)) ? NHIP_XU_LY : false),
  });
}

export function useChiTietPhien(id: string | undefined) {
  return useQuery<Phien, ApiError>({
    queryKey: KHOA_BIEN_BAN.chiTiet(id ?? ''),
    queryFn: () => bienBanRepository.chiTiet(id!),
    enabled: Boolean(id),
    staleTime: 5_000,
    retry: nenThuLai,
    refetchInterval: (q) => (canTuLamMoi(q.state.data) ? NHIP_XU_LY : false),
  });
}

/** Bản chép lời chỉ đọc từ lúc máy đã chép xong: trước đó không có gì và hỏi chỉ tốn lượt. */
export function useDoanPhien(id: string | undefined, status: TrangThaiPhien | undefined) {
  return useQuery<Doan[], ApiError>({
    queryKey: KHOA_BIEN_BAN.doan(id ?? ''),
    queryFn: () => bienBanRepository.doan(id!),
    enabled: Boolean(id && status && coBanChepLoi(status)),
    staleTime: 30_000,
    retry: nenThuLai,
  });
}

/**
 * URL ký để nghe lại. Không tự làm mới theo thời gian: đổi `src` giữa lúc đang phát là ngắt tiếng. Trình phát tự xin URL
 * mới khi thẻ `audio` báo lỗi, nên URL hết hạn được xử lý đúng một chỗ. `gcTime: 0` để URL không nằm lại trong cache.
 */
export function useAmThanhPhien(id: string) {
  return useQuery<AmThanhKy, ApiError>({
    queryKey: KHOA_BIEN_BAN.amThanh(id),
    queryFn: () => bienBanRepository.amThanh(id),
    staleTime: Infinity,
    gcTime: 0,
    retry: nenThuLai,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Thao tác ghi. Không tự bật toast: phía gọi quyết câu báo và chỗ hiện lỗi. */
export function useGhiPhien() {
  const qc = useQueryClient();
  const lamMoiDs = () => void qc.invalidateQueries({ queryKey: KHOA_BIEN_BAN.dsGoc });
  const lamMoiPhien = (id: string) => {
    void qc.invalidateQueries({ queryKey: KHOA_BIEN_BAN.chiTiet(id) });
    lamMoiDs();
  };
  return {
    tao: useMutation<Phien, ApiError, { vao: PhienTao; onTienTrinh: (phanTram: number) => void; signal: AbortSignal }>({
      mutationFn: ({ vao, onTienTrinh, signal }) => bienBanRepository.tao(vao, { onTienTrinh, signal }),
      onSuccess: lamMoiDs,
    }),
    sua: useMutation<Phien, ApiError, { id: string; vao: PhienSua }>({
      mutationFn: ({ id, vao }) => bienBanRepository.sua(id, vao),
      onSuccess: (p) => {
        qc.setQueryData(KHOA_BIEN_BAN.chiTiet(p.id), p);
        lamMoiDs();
      },
    }),
    xoa: useMutation<void, ApiError, string>({
      mutationFn: (id) => bienBanRepository.xoa(id),
      onSuccess: (_kq, id) => {
        qc.removeQueries({ queryKey: KHOA_BIEN_BAN.chiTiet(id) });
        qc.removeQueries({ queryKey: KHOA_BIEN_BAN.doan(id) });
        // Bỏ ngay khỏi danh sách đã nạp: đọc lại nhiều trang mất một nhịp, người vừa xoá không nên thấy dòng đó hiện lại.
        qc.setQueriesData<InfiniteData<TrangPhien>>({ queryKey: KHOA_BIEN_BAN.dsGoc }, (cu) =>
          cu && { ...cu, pages: cu.pages.map((p) => ({ ...p, items: p.items.filter((x) => x.id !== id) })) },
        );
        lamMoiDs();
      },
    }),
    /** Đổi người nói làm đổi thời lượng nói của từng người, nên đọc lại cả phiên. */
    suaDoan: useMutation<Doan, ApiError, { id: string; doanId: string; vao: DoanSua }>({
      mutationFn: ({ id, doanId, vao }) => bienBanRepository.suaDoan(id, doanId, vao),
      onSuccess: (d, { id }) => {
        qc.setQueryData<Doan[]>(KHOA_BIEN_BAN.doan(id), (cu) => cu?.map((x) => (x.id === d.id ? d : x)));
        lamMoiPhien(id);
      },
    }),
    thuLai: useMutation<void, ApiError, string>({
      mutationFn: (id) => bienBanRepository.thuLai(id),
      onSuccess: (_kq, id) => {
        void qc.invalidateQueries({ queryKey: KHOA_BIEN_BAN.doan(id) });
        lamMoiPhien(id);
      },
    }),
    tomTatLai: useMutation<void, ApiError, string>({
      mutationFn: (id) => bienBanRepository.tomTatLai(id),
      onSuccess: (_kq, id) => lamMoiPhien(id),
    }),
    suaTomTat: useMutation<void, ApiError, { id: string; vao: TomTatGhi }>({
      mutationFn: ({ id, vao }) => bienBanRepository.suaTomTat(id, vao),
      onSuccess: (_kq, { id }) => lamMoiPhien(id),
    }),
    xuat: useMutation<{ blob: Blob; ten: string }, ApiError, { id: string; dinhDang: DinhDangXuat; tenMacDinh: string }>({
      mutationFn: ({ id, dinhDang, tenMacDinh }) => bienBanRepository.xuat(id, dinhDang, tenMacDinh),
    }),
  };
}
